import { EntityNameEnum } from '@monetize/types/app';
import {
  FilterOptionType,
  FilterRangeValue,
  FilterType,
  FilterTypeOperator,
} from '@monetize/types/common';
import isString from 'lodash/isString';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetIntegrationLogInfiniteLoad } from '../api/settingsService';
import { composeFiltersQuery } from '../api/utils';
import {
  buildFilterParamsRequestObject,
  filtersToSearchParams,
  searchParamsToFilters,
} from '../utils';

/** Column id of the table's single timestamp filter. */
const TIMESTAMP_FILTER_KEY = 'lastUpdatedAt';
const FROM_TIMESTAMP_KEY = 'fromLastUpdatedAt';
const TO_TIMESTAMP_KEY = 'toLastUpdatedAt';

const START_OF_DAY_TIME = 'T00:00:00.000Z';
const END_OF_DAY_TIME = 'T23:59:59.999Z';

/**
 * Request/URL filter keys, which are also the shareable-link contract. The
 * timestamp column is filtered through the two bound params the API exposes
 * rather than an operator on `lastUpdatedAt`.
 */
const URL_FILTER_OPTIONS: FilterOptionType[] = [
  {
    title: 'From',
    key: FROM_TIMESTAMP_KEY,
    operator: FilterTypeOperator.EQUAL,
  },
  { title: 'To', key: TO_TIMESTAMP_KEY, operator: FilterTypeOperator.EQUAL },
  { title: 'Object', key: 'entityName', operator: FilterTypeOperator.IN },
  { title: 'Operation', key: 'eventType', operator: FilterTypeOperator.IN },
  { title: 'Status', key: 'status', operator: FilterTypeOperator.IN },
  {
    title: 'External System',
    key: 'targetSystemType',
    operator: FilterTypeOperator.IN,
  },
  {
    title: 'Source Record ID',
    key: 'sourceId',
    operator: FilterTypeOperator.EQUAL,
  },
];

const DEFAULT_FILTERS: FilterType[] = [
  {
    key: 'entityName',
    operator: FilterTypeOperator.IN,
    value: Object.values(EntityNameEnum).filter(
      (entityName) => entityName !== EntityNameEnum.QUOTE_OFFERING,
    ),
  } as FilterType,
];

/**
 * Resolve a date filter's operator + min/max into the inclusive bounds the API
 * accepts. Only `min` is populated for LT/LTE comparisons, and the API has no
 * exclusive bounds, so LT/GT are treated as their inclusive counterparts.
 */
const getTimestampBounds = (
  operator: FilterTypeOperator | undefined,
  value: FilterRangeValue,
): { from?: string; to?: string } => {
  const { min, max } = value;
  switch (operator) {
    case FilterTypeOperator.GT:
    case FilterTypeOperator.GTE:
      return { from: min };
    case FilterTypeOperator.LT:
    case FilterTypeOperator.LTE:
      return { to: max || min };
    case FilterTypeOperator.EQ:
      // A date with no time selected should match the whole day
      return {
        from: min,
        to: min?.endsWith(START_OF_DAY_TIME)
          ? min.replace(START_OF_DAY_TIME, END_OF_DAY_TIME)
          : max || min,
      };
    default:
      return { from: min, to: max };
  }
};

/**
 * Replace the table's single timestamp filter with the `fromLastUpdatedAt` /
 * `toLastUpdatedAt` pair used by both the API and the page URL.
 */
export const expandTimestampFilter = (filters: FilterType[]): FilterType[] =>
  filters.flatMap<FilterType>((filter) => {
    if (filter.key !== TIMESTAMP_FILTER_KEY) {
      return [filter];
    }
    const { from, to } = getTimestampBounds(
      filter.operator,
      (filter.value ?? {}) as FilterRangeValue,
    );
    const bounds: FilterType[] = [];
    if (from) {
      bounds.push({
        key: FROM_TIMESTAMP_KEY,
        operator: FilterTypeOperator.EQUAL,
        value: from,
      } as FilterType);
    }
    if (to) {
      bounds.push({
        key: TO_TIMESTAMP_KEY,
        operator: FilterTypeOperator.EQUAL,
        value: to,
      } as FilterType);
    }
    return bounds;
  });

/**
 * Inverse of {@link expandTimestampFilter}: fold the two bound params back into
 * the single date filter the table's timestamp column owns.
 */
export const collapseTimestampFilters = (
  filters: FilterType[],
): FilterType[] => {
  const otherFilters: FilterType[] = [];
  let from: string | undefined;
  let to: string | undefined;

  filters.forEach((filter) => {
    if (filter.key === FROM_TIMESTAMP_KEY && isString(filter.value)) {
      from = filter.value;
    } else if (filter.key === TO_TIMESTAMP_KEY && isString(filter.value)) {
      to = filter.value;
    } else {
      otherFilters.push(filter);
    }
  });

  if (!from && !to) {
    return otherFilters;
  }
  if (from && to) {
    return [
      ...otherFilters,
      {
        key: TIMESTAMP_FILTER_KEY,
        operator: FilterTypeOperator.BETWEEN,
        value: { min: from, max: to },
      } as FilterType,
    ];
  }
  // Single-bound filters keep their date in `min`, which is where the date
  // filter popover reads it back from for every comparison.
  return [
    ...otherFilters,
    {
      key: TIMESTAMP_FILTER_KEY,
      operator: from ? FilterTypeOperator.GTE : FilterTypeOperator.LTE,
      value: { min: from ?? to, max: '' },
    } as FilterType,
  ];
};

/**
 * Filters the list opens on. A link that carries its own filters — e.g. the
 * `?sourceId=` link from a record's integration popover — already targets what
 * the user asked for, so it is honoured exactly as shared; only an unfiltered
 * visit falls back to {@link DEFAULT_FILTERS}.
 */
export const getInitialFilters = (
  searchParams: URLSearchParams,
): FilterType[] => {
  const urlFilters = collapseTimestampFilters(
    searchParamsToFilters(searchParams, URL_FILTER_OPTIONS),
  );
  return urlFilters.length > 0 ? urlFilters : DEFAULT_FILTERS;
};

export function useIntegrationLogData(limit = 25) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate filters from the URL on first render so the list can be
  // deep-linked/shared with filters (e.g. ?sourceId=...) pre-applied.
  const [filters, setFiltersState] = useState<FilterType[]>(() =>
    getInitialFilters(searchParams),
  );

  const requestFilters = useMemo(
    () => expandTimestampFilter(filters),
    [filters],
  );

  const setFilters = useCallback(
    (nextFilters: FilterType[]) => {
      setFiltersState(nextFilters);
      // Keep the URL in sync with the applied filters (replace, so filtering
      // does not spam the browser history).
      setSearchParams(
        filtersToSearchParams(expandTimestampFilter(nextFilters)),
        {
          replace: true,
        },
      );
    },
    [setSearchParams],
  );

  const filterParams = useMemo(
    () => buildFilterParamsRequestObject(requestFilters),
    [requestFilters],
  );

  const payload = {
    limit,
    ...composeFiltersQuery(filterParams),
  };

  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isError,
    fetchNextPage,
    refetch,
  } = useGetIntegrationLogInfiniteLoad(payload, {
    meta: { showErrorToast: true },
  });

  const logData = data?.pages.flatMap((page) => page.content) || [];

  return {
    filters,
    filterParams,
    logData,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isError,
    refetch,
    setFilters,
    fetchNextPage,
  };
}
